/**
 * Section Layout Type Definitions
 * 
 * This file defines the available layout options for each section type.
 * Designed for scalability - add new sections/layouts by extending these definitions.
 */

// ============================================================================
// HERO SECTION LAYOUTS
// ============================================================================

export type HeroLayoutId = 'centered' | 'left-aligned' | 'split' | 'gradient-bold';

export interface HeroLayoutOption {
    id: HeroLayoutId;
    label: string;
    description: string;
    icon: string;
    supportsImage: boolean;
}

export const HERO_LAYOUTS: HeroLayoutOption[] = [
    {
        id: 'centered',
        label: 'Centered',
        description: 'Clean centered layout with social proof',
        icon: 'Bars3CenterLeftIcon',
        supportsImage: false,
    },
    {
        id: 'left-aligned',
        label: 'Left Aligned',
        description: 'Content left with image/visual on right',
        icon: 'Squares2X2Icon',
        supportsImage: true,
    },
    {
        id: 'split',
        label: 'Split View',
        description: '50/50 text and visual layout',
        icon: 'ViewColumnsIcon',
        supportsImage: true,
    },
    {
        id: 'gradient-bold',
        label: 'Gradient Bold',
        description: 'High-contrast gradient with bold typography',
        icon: 'SparklesIcon',
        supportsImage: false,
    },
];

// ============================================================================
// CTA BUTTON STYLES
// ============================================================================

export type ButtonStyleId = 'rounded' | 'square' | 'outline' | 'gradient';

export interface ButtonStyleOption {
    id: ButtonStyleId;
    label: string;
    description: string;
    // Base classes applied to the button
    className: string;
    // Whether this style needs special handling
    isOutline?: boolean;
    isGradient?: boolean;
}

export const BUTTON_STYLES: ButtonStyleOption[] = [
    {
        id: 'rounded',
        label: 'Rounded Pill',
        description: 'Classic rounded button',
        className: 'px-8 py-4 rounded-full font-medium shadow-lg hover:shadow-xl transition-all',
    },
    {
        id: 'square',
        label: 'Square Modern',
        description: 'Professional square corners',
        className: 'px-8 py-4 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all',
    },
    {
        id: 'outline',
        label: 'Outline',
        description: 'Transparent with border',
        className: 'px-8 py-4 rounded-full font-medium border-2 bg-transparent hover:bg-opacity-10 transition-all',
        isOutline: true,
    },
    {
        id: 'gradient',
        label: 'Gradient',
        description: 'Vibrant gradient effect',
        className: 'px-8 py-4 rounded-2xl font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all',
        isGradient: true,
    },
];

export const DEFAULT_BUTTON_STYLE: ButtonStyleId = 'rounded';

// Helper function to get button styles based on primary color
export function getButtonStyles(styleId: ButtonStyleId, primaryColor: string): React.CSSProperties {
    const style = BUTTON_STYLES.find(s => s.id === styleId);
    
    if (style?.isOutline) {
        return {
            borderColor: primaryColor,
            color: primaryColor,
            backgroundColor: 'transparent',
        };
    }
    
    if (style?.isGradient) {
        // Create gradient from primary color to a lighter shade
        return {
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${adjustColor(primaryColor, 30)} 100%)`,
            color: 'white',
        };
    }
    
    return {
        backgroundColor: primaryColor,
        color: 'white',
    };
}

// Helper to adjust color brightness
function adjustColor(hex: string, percent: number): string {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = (num >> 8 & 0x00FF) + amt;
    const B = (num & 0x0000FF) + amt;
    return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
        (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
        (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
}

// ============================================================================
// SECTION LAYOUT CONFIGURATION
// ============================================================================

export interface SectionLayoutConfig {
    imageUrl?: string;
    imagePosition?: 'left' | 'right';
    backgroundType?: 'gradient' | 'solid' | 'image';
    backgroundColor?: string;
    // Hero-specific controls
    showTrustBar?: boolean;
    trustBarText?: string;
    trustBarAvatarSeed?: number;
    showDecorations?: boolean;
    heroBackground?: 'white' | 'gradient' | 'colored';
    heroBackgroundColor?: string;
    embedSignupInHero?: boolean;
    validationHeroTemplate?: string;
    heroFormTemplate?: string;
    // Features-specific controls
    featureIcons?: Record<number, string>;
    featuresTitle?: string;
    featuresSubtitle?: string;
    // Logo display controls (stored in navbar section)
    showLogoText?: boolean;
    logoText?: string;
    logoHeight?: number;
    // Footer controls (stored in footer section)
    footerBgColor?: string;
    footerLinks?: Array<{ label: string; url: string }>;
    socialLinks?: Record<string, string>;
}

// Default configurations
export const DEFAULT_HERO_LAYOUT: HeroLayoutId = 'centered';

export const DEFAULT_LAYOUT_CONFIG: SectionLayoutConfig = {
    imagePosition: 'right',
    backgroundType: 'gradient',
};

// ============================================================================
// FEATURES SECTION LAYOUTS
// ============================================================================

export type FeaturesLayoutId = 'grid-cards' | 'bento-grid' | 'alternating' | 'minimal-list' | 'icon-left' | 'centered-icons';

export interface FeaturesLayoutOption {
    id: FeaturesLayoutId;
    label: string;
    description: string;
    icon: string;
}

export const FEATURES_LAYOUTS: FeaturesLayoutOption[] = [
    {
        id: 'grid-cards',
        label: 'Grid Cards',
        description: 'Classic 3-column card layout with icons',
        icon: 'Squares2X2Icon',
    },
    {
        id: 'bento-grid',
        label: 'Bento Grid',
        description: 'Modern Apple-style asymmetric grid',
        icon: 'ViewColumnsIcon',
    },
    {
        id: 'alternating',
        label: 'Alternating Rows',
        description: 'Feature + visual alternating sides',
        icon: 'Bars3Icon',
    },
    {
        id: 'minimal-list',
        label: 'Minimal List',
        description: 'Clean numbered feature list',
        icon: 'ListBulletIcon',
    },
    {
        id: 'icon-left',
        label: 'Icon Left',
        description: 'Horizontal cards with icon on the left',
        icon: 'ArrowRightIcon',
    },
    {
        id: 'centered-icons',
        label: 'Centered Icons',
        description: 'Large centered icon with text below',
        icon: 'SparklesIcon',
    },
];

export const DEFAULT_FEATURES_LAYOUT: FeaturesLayoutId = 'grid-cards';

// ============================================================================
// CURATED ICON SET FOR FEATURE ICON PICKER
// ============================================================================
export const FEATURE_ICON_OPTIONS: { name: string; label: string }[] = [
    { name: 'CheckCircleIcon', label: 'Check Circle' },
    { name: 'StarIcon', label: 'Star' },
    { name: 'SparklesIcon', label: 'Sparkles' },
    { name: 'BoltIcon', label: 'Bolt' },
    { name: 'RocketLaunchIcon', label: 'Rocket' },
    { name: 'ShieldCheckIcon', label: 'Shield' },
    { name: 'LockClosedIcon', label: 'Lock' },
    { name: 'GlobeAltIcon', label: 'Globe' },
    { name: 'ChatBubbleLeftRightIcon', label: 'Chat' },
    { name: 'HeartIcon', label: 'Heart' },
    { name: 'LightBulbIcon', label: 'Light Bulb' },
    { name: 'CubeIcon', label: 'Cube' },
    { name: 'CubeTransparentIcon', label: 'Cube 3D' },
    { name: 'CpuChipIcon', label: 'CPU Chip' },
    { name: 'CommandLineIcon', label: 'Terminal' },
    { name: 'CloudIcon', label: 'Cloud' },
    { name: 'ChartBarIcon', label: 'Chart Bar' },
    { name: 'PresentationChartLineIcon', label: 'Chart Line' },
    { name: 'CurrencyDollarIcon', label: 'Dollar' },
    { name: 'UserGroupIcon', label: 'Users' },
    { name: 'UsersIcon', label: 'Team' },
    { name: 'ClockIcon', label: 'Clock' },
    { name: 'BellIcon', label: 'Bell' },
    { name: 'EnvelopeIcon', label: 'Email' },
    { name: 'PhoneIcon', label: 'Phone' },
    { name: 'MapPinIcon', label: 'Location' },
    { name: 'DocumentTextIcon', label: 'Document' },
    { name: 'PencilSquareIcon', label: 'Edit' },
    { name: 'AdjustmentsHorizontalIcon', label: 'Settings' },
    { name: 'WrenchScrewdriverIcon', label: 'Tools' },
    { name: 'PaintBrushIcon', label: 'Design' },
    { name: 'PhotoIcon', label: 'Image' },
    { name: 'VideoCameraIcon', label: 'Video' },
    { name: 'MusicalNoteIcon', label: 'Music' },
    { name: 'EyeIcon', label: 'Eye' },
    { name: 'HandThumbUpIcon', label: 'Thumbs Up' },
    { name: 'TrophyIcon', label: 'Trophy' },
    { name: 'FireIcon', label: 'Fire' },
    { name: 'BeakerIcon', label: 'Beaker' },
    { name: 'AcademicCapIcon', label: 'Education' },
    { name: 'BuildingOfficeIcon', label: 'Office' },
    { name: 'Cog6ToothIcon', label: 'Gear' },
    { name: 'SignalIcon', label: 'Signal' },
    { name: 'WifiIcon', label: 'WiFi' },
    { name: 'ArrowTrendingUpIcon', label: 'Trending Up' },
    { name: 'ArrowPathIcon', label: 'Refresh' },
    { name: 'FunnelIcon', label: 'Funnel' },
    { name: 'MagnifyingGlassIcon', label: 'Search' },
    { name: 'SquaresStackIcon', label: 'Layers' },
];

// ============================================================================
// TESTIMONIALS SECTION LAYOUTS
// ============================================================================

export type TestimonialsLayoutId = 'cards' | 'spotlight' | 'carousel' | 'wall';

export interface TestimonialsLayoutOption {
    id: TestimonialsLayoutId;
    label: string;
    description: string;
    icon: string;
}

export const TESTIMONIALS_LAYOUTS: TestimonialsLayoutOption[] = [
    {
        id: 'cards',
        label: 'Cards Grid',
        description: 'Classic 2-column card layout with stars',
        icon: 'Squares2X2Icon',
    },
    {
        id: 'spotlight',
        label: 'Quote Spotlight',
        description: 'One large featured testimonial at a time',
        icon: 'ChatBubbleLeftRightIcon',
    },
    {
        id: 'carousel',
        label: 'Carousel',
        description: 'Swipeable cards with navigation arrows',
        icon: 'ArrowsRightLeftIcon',
    },
    {
        id: 'wall',
        label: 'Wall of Love',
        description: 'Masonry grid of many compact testimonials',
        icon: 'RectangleGroupIcon',
    },
];

export const DEFAULT_TESTIMONIALS_LAYOUT: TestimonialsLayoutId = 'cards';

// ============================================================================
// EMAIL SIGNUP SECTION LAYOUTS
// ============================================================================

export type EmailSignupLayoutId = 'centered' | 'split' | 'minimal' | 'card';

export interface EmailSignupLayoutOption {
    id: EmailSignupLayoutId;
    label: string;
    description: string;
    icon: string;
    supportsImage: boolean;
}

export const EMAIL_SIGNUP_LAYOUTS: EmailSignupLayoutOption[] = [
    {
        id: 'centered',
        label: 'Centered Classic',
        description: 'Centered heading with stacked form below',
        icon: 'Bars3CenterLeftIcon',
        supportsImage: false,
    },
    {
        id: 'split',
        label: 'Split View',
        description: '50/50 layout with visual on one side',
        icon: 'ViewColumnsIcon',
        supportsImage: true,
    },
    {
        id: 'minimal',
        label: 'Minimal Inline',
        description: 'Sleek single-line compact form',
        icon: 'MinusIcon',
        supportsImage: false,
    },
    {
        id: 'card',
        label: 'Floating Card',
        description: 'Elevated card with gradient background',
        icon: 'Square2StackIcon',
        supportsImage: false,
    },
];

export const DEFAULT_EMAIL_SIGNUP_LAYOUT: EmailSignupLayoutId = 'centered';

// ============================================================================
// NAVBAR SECTION LAYOUTS
// ============================================================================

export type NavbarLayoutId = 'classic' | 'centered' | 'minimal' | 'floating';

export interface NavbarLayoutOption {
    id: NavbarLayoutId;
    label: string;
    description: string;
    icon: string;
}

export const NAVBAR_LAYOUTS: NavbarLayoutOption[] = [
    {
        id: 'classic',
        label: 'Classic',
        description: 'Logo left, navigation center, CTA right',
        icon: 'Bars3Icon',
    },
    {
        id: 'centered',
        label: 'Centered',
        description: 'Centered logo with nav links below',
        icon: 'Bars3CenterLeftIcon',
    },
    {
        id: 'minimal',
        label: 'Minimal',
        description: 'Logo left, CTA right — clean and focused',
        icon: 'MinusIcon',
    },
    {
        id: 'floating',
        label: 'Floating Pill',
        description: 'Modern floating navbar with glass effect',
        icon: 'SparklesIcon',
    },
];

export const DEFAULT_NAVBAR_LAYOUT: NavbarLayoutId = 'classic';

// ============================================================================
// FAQ SECTION LAYOUTS
// ============================================================================

export type FAQLayoutId = 'accordion' | 'two-column' | 'side-by-side' | 'cards-grid';

export interface FAQLayoutOption {
    id: FAQLayoutId;
    label: string;
    description: string;
    icon: string;
}

export const FAQ_LAYOUTS: FAQLayoutOption[] = [
    {
        id: 'accordion',
        label: 'Accordion',
        description: 'Classic expandable accordion with smooth animations',
        icon: 'Bars3Icon',
    },
    {
        id: 'two-column',
        label: 'Two Column',
        description: 'Questions split across two columns',
        icon: 'ViewColumnsIcon',
    },
    {
        id: 'side-by-side',
        label: 'Side by Side',
        description: 'Question title left, answers right',
        icon: 'Squares2X2Icon',
    },
    {
        id: 'cards-grid',
        label: 'Cards Grid',
        description: 'Each FAQ as a standalone card in a grid',
        icon: 'RectangleGroupIcon',
    },
];

export const DEFAULT_FAQ_LAYOUT: FAQLayoutId = 'accordion';

// ============================================================================
// FOOTER SECTION LAYOUTS
// ============================================================================

export type FooterLayoutId = 'simple' | 'columns' | 'centered-rich';

export interface FooterLayoutOption {
    id: FooterLayoutId;
    label: string;
    description: string;
    icon: string;
}

export const FOOTER_LAYOUTS: FooterLayoutOption[] = [
    {
        id: 'simple',
        label: 'Simple',
        description: 'Clean logo + copyright line',
        icon: 'MinusIcon',
    },
    {
        id: 'columns',
        label: 'Columns',
        description: 'Multi-column with links and social icons',
        icon: 'ViewColumnsIcon',
    },
    {
        id: 'centered-rich',
        label: 'Centered Rich',
        description: 'Centered nav links, social row, copyright',
        icon: 'Bars3CenterLeftIcon',
    },
];

export const DEFAULT_FOOTER_LAYOUT: FooterLayoutId = 'simple';

// Social media platform definitions
export const SOCIAL_PLATFORMS = [
    { key: 'twitter', label: 'Twitter / X', icon: 'ChatBubbleLeftRightIcon' },
    { key: 'linkedin', label: 'LinkedIn', icon: 'UserGroupIcon' },
    { key: 'github', label: 'GitHub', icon: 'CommandLineIcon' },
    { key: 'instagram', label: 'Instagram', icon: 'CameraIcon' },
    { key: 'facebook', label: 'Facebook', icon: 'GlobeAltIcon' },
    { key: 'youtube', label: 'YouTube', icon: 'PlayIcon' },
] as const;

// Export a type for all layout IDs
export type SectionLayoutId = HeroLayoutId | FeaturesLayoutId | TestimonialsLayoutId | EmailSignupLayoutId | NavbarLayoutId | FAQLayoutId | FooterLayoutId;
